/**
 * Basis HTTP-Client für API-Kommunikation
 * Enthält Auth-Handling, Token-Refresh und generische HTTP-Methoden
 */
import config from "../../config/environment";

/**
 * Typisierter API-Fehler mit Status-Code und Backend-Detail-Nachricht.
 * Erlaubt saubere Unterscheidung von Netzwerk- vs. Business-Fehlern in Catch-Blocks.
 */
export class ApiError extends Error {
  constructor(
    public readonly detail: string,
    public readonly statusCode: number,
  ) {
    super(detail);
    this.name = "ApiError";
  }
}

const BASE_URL = config.apiBaseUrl;

/**
 * Ruft bei einem 401 einen echten Silent-Renew auf (statt nur den gecachten Token
 * erneut zu lesen) und übernimmt das dabei erneuerte Token in den Client.
 * Schlägt der Renew fehl (z.B. abgelaufene Session, kein Refresh-Token mehr gültig),
 * meldet das der zentralen Auth-Stelle über `notifySessionExpired`, damit dort –
 * unabhängig von der aufrufenden Domäne – einheitlich reagiert werden kann.
 */
const handleTokenRefresh = async (): Promise<boolean> => {
  const authProvider = (window as any).__AUTH_PROVIDER__;
  try {
    if (authProvider?.getAccessToken) {
      const token = await authProvider.getAccessToken();
      if (token) {
        api.setAuthToken(token);
        return true;
      }
    }
  } catch {
    // fällt durch zu notifySessionExpired
  }
  authProvider?.notifySessionExpired?.();
  return false;
};

/**
 * Hilfsfunktion für Token-Handling
 * Wird auch von Upload-Funktionen (Shapefile, ALKIS) genutzt
 */
export const getAuthHeaders = async (): Promise<Record<string, string>> => {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  // Auth-Token hinzufügen, falls vorhanden
  if (api.authToken) {
    headers["Authorization"] = `Bearer ${api.authToken}`;
  } else {
    // Versuche Token vom AuthProvider zu holen
    const authProvider = (window as any).__AUTH_PROVIDER__;

    if (authProvider && authProvider.getAccessToken) {
      try {
        const token = await authProvider.getAccessToken();

        if (token) {
          api.authToken = token;
          headers["Authorization"] = `Bearer ${token}`;
        }
      } catch (error) {}
    }
  }

  return headers;
};

type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

/** Parst den Response-Body robust: kein Inhalt/kein JSON → `null`. */
const parseResponseBody = async (response: Response): Promise<any> => {
  if (response.status === 204) return null;

  const contentType = response.headers.get("content-type");
  if (!contentType || !contentType.includes("application/json")) return null;

  const text = await response.text();
  if (!text || text.trim() === "") return null;

  return JSON.parse(text);
};

/**
 * Zentrale Request-Funktion für alle HTTP-Methoden.
 * Einziger Ort, an dem der 401-Retry (Token-Refresh) sowie der Fallback bei
 * endgültig gescheitertem Refresh behandelt werden.
 */
const request = async (
  method: HttpMethod,
  endpoint: string,
  body: any | undefined,
  options: RequestInit | undefined,
  retryCount: number,
): Promise<any> => {
  const headers = await getAuthHeaders();

  if (options?.headers) {
    Object.entries(options.headers).forEach(([key, value]) => {
      if (typeof value === "string") {
        headers[key] = value;
      }
    });
  }

  const isFormEncoded =
    headers["Content-Type"] === "application/x-www-form-urlencoded";
  const requestBody =
    body === undefined
      ? undefined
      : isFormEncoded
        ? body
        : JSON.stringify(body);

  const response = await fetch(`${BASE_URL}${endpoint}`, {
    ...options,
    method,
    headers,
    ...(method !== "GET" && method !== "DELETE" ? { body: requestBody } : {}),
  });

  if (!response.ok) {
    if (response.status === 401 && retryCount === 0) {
      const refreshSuccess = await handleTokenRefresh();
      if (refreshSuccess) {
        return request(method, endpoint, body, options, retryCount + 1);
      }
    }

    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(
      errorData.detail || response.statusText,
      response.status,
    );
  }

  return parseResponseBody(response);
};

/**
 * Basis API-Instanz für HTTP-Requests
 * Exportiert für direkte Nutzung in Domain-Services
 */
export const api = {
  // Auth Token für alle Requests
  authToken: "",

  // Setze Authorization Header
  setAuthToken(token: string) {
    this.authToken = token;
  },

  // Default Headers
  defaultHeaders: {} as Record<string, string>,

  async get(
    endpoint: string,
    options?: RequestInit,
    retryCount: number = 0,
  ): Promise<any> {
    return request("GET", endpoint, undefined, options, retryCount);
  },

  async post(
    endpoint: string,
    body?: any,
    options?: RequestInit,
    retryCount: number = 0,
  ): Promise<any> {
    return request("POST", endpoint, body, options, retryCount);
  },

  async put(
    endpoint: string,
    body?: any,
    options?: RequestInit,
    retryCount: number = 0,
  ): Promise<any> {
    return request("PUT", endpoint, body, options, retryCount);
  },

  async patch(
    endpoint: string,
    body?: any,
    options?: RequestInit,
    retryCount: number = 0,
  ): Promise<any> {
    return request("PATCH", endpoint, body, options, retryCount);
  },

  async delete(
    endpoint: string,
    options?: RequestInit,
    retryCount: number = 0,
  ): Promise<any> {
    return request("DELETE", endpoint, undefined, options, retryCount);
  },
};

/** Basis-URL für Upload-Funktionen */
export const getBaseUrl = () => BASE_URL;
